import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * 표 위 가로 스크롤 막대(10-14 #249 이현숙 — 「화면 오른쪽을 보려고 화면 비율을 줄이면 글자가 안 보인다 · 메뉴 위에 가로 스크롤」).
 *
 * 표 상자(`target`)의 가로 스크롤을 위에 한 줄 더 비춘다 — 둘 중 어느 쪽을 밀어도 같이 움직인다.
 * 표가 화면 폭 안에 들어오면(넘치는 칸이 없으면) 아무것도 그리지 않는다.
 */
export function TopScrollbar({ target }: { target: RefObject<HTMLElement | null> }) {
  const bar = useRef<HTMLDivElement>(null);
  const [w, setW] = useState({ inner: 0, outer: 0 });
  const syncing = useRef(false);

  useEffect(() => {
    const el = target.current;
    if (!el) return;
    const measure = () => setW((cur) =>
      cur.inner === el.scrollWidth && cur.outer === el.clientWidth ? cur : { inner: el.scrollWidth, outer: el.clientWidth });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    const onScroll = () => {
      if (syncing.current) { syncing.current = false; return; }
      if (bar.current && bar.current.scrollLeft !== el.scrollLeft) { syncing.current = true; bar.current.scrollLeft = el.scrollLeft; }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { ro.disconnect(); el.removeEventListener('scroll', onScroll); };
  }, [target]);

  if (w.inner <= w.outer + 1) return null;
  return (
    <div
      ref={bar}
      className="overflow-x-auto overflow-y-hidden"
      style={{ height: 14 }}
      title="옆으로 밀어 표의 오른쪽 칸을 봅니다"
      onScroll={(e) => {
        const el = target.current;
        if (!el) return;
        if (syncing.current) { syncing.current = false; return; }
        if (el.scrollLeft !== e.currentTarget.scrollLeft) { syncing.current = true; el.scrollLeft = e.currentTarget.scrollLeft; }
      }}
    >
      <div style={{ width: w.inner, height: 1 }} />
    </div>
  );
}
